import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tracksApi } from '../api/tracks';
import { Search, Play, Clock, Heart, Shuffle } from 'lucide-react';
import { usePlayerStore } from '../store/playerStore';
import PlayingAnimation from '../components/PlayingAnimation';
import { useLikes } from '../hooks/useLikes';

type SortOption = 'recent' | 'popular' | 'recentlyPlayed' | 'shuffle';

export default function TracksPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortOption | null>('popular');
  const [shuffleKey, setShuffleKey] = useState(0);
  const playTrackWithQueue = usePlayerStore((state) => state.playTrackWithQueue);
  const currentTrack = usePlayerStore((state) => state.currentTrack);
  const isPlaying = usePlayerStore((state) => state.isPlaying);

  const { data, isLoading } = useQuery({
    queryKey: ['tracks', page, search, sortBy, sortBy === 'shuffle' ? shuffleKey : null],
    queryFn: () => tracksApi.getAll({ page, limit: 20, search: search || undefined, sort: sortBy || 'popular' }),
    placeholderData: (previousData) => previousData,
  });

  // Get track IDs for likes checking
  const trackIds = useMemo(() => data?.data.map((t) => t._id) || [], [data]);
  const { isLiked, toggleLike, isAuthenticated } = useLikes(trackIds);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="border-b border-white/10 pb-4 sm:pb-6">
        <h1 className="text-2xl sm:text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-white via-primary-200 to-secondary-200 bg-clip-text text-transparent mb-1 sm:mb-2">
          {t('tracks.title')}
        </h1>
        <p className="text-gray-400 text-sm sm:text-lg">{t('tracks.subtitle')}</p>
      </div>

      {/* Search & Sort */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder={t('tracks.search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-dark-800/50 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-sm sm:text-base"
          />
        </div>

        {/* Sort Tabs */}
        <div className="flex gap-1 bg-white/5 p-1 rounded-xl border border-white/10 overflow-x-auto">
          <button
            onClick={() => {
              if (sortBy === 'shuffle') {
                setShuffleKey(k => k + 1);
              } else {
                setSortBy('shuffle');
              }
            }}
            className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all whitespace-nowrap flex items-center gap-1.5 ${
              sortBy === 'shuffle'
                ? 'bg-primary-500 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Shuffle size={14} />
            {t('tracks.sort.shuffle')}
          </button>
          <button
            onClick={() => setSortBy('popular')}
            className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
              sortBy === 'popular'
                ? 'bg-primary-500 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            {t('tracks.sort.popular')}
          </button>
          <button
            onClick={() => setSortBy('recentlyPlayed')}
            className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
              sortBy === 'recentlyPlayed'
                ? 'bg-primary-500 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            {t('tracks.sort.recentlyPlayed')}
          </button>
          <button
            onClick={() => setSortBy('recent')}
            className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
              sortBy === 'recent'
                ? 'bg-primary-500 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            {t('tracks.sort.recent')}
          </button>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          <p className="mt-4 text-gray-400">{t('tracks.loading')}</p>
        </div>
      )}

      {/* Tracks List */}
      {!isLoading && data && (
        <>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-white/10 overflow-hidden">
            <div className="divide-y divide-white/5">
              {data.data.map((track, index) => (
                <Link
                  key={track._id}
                  to={`/tracks/${track._id}`}
                  className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 hover:bg-white/10 transition-all group ${
                    currentTrack?._id === track._id ? 'bg-primary-500/10 border-l-4 border-primary-500' : ''
                  }`}
                >
                  {/* Track Number or Playing Animation - hidden on mobile */}
                  <div className="hidden sm:flex w-8 items-center justify-center">
                    {currentTrack?._id === track._id && isPlaying ? (
                      <PlayingAnimation />
                    ) : (
                      <span className="text-gray-400 text-sm font-medium">
                        {(page - 1) * 20 + index + 1}
                      </span>
                    )}
                  </div>

                  {/* Play Button & Image */}
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg sm:rounded-xl flex items-center justify-center text-white text-lg sm:text-xl overflow-hidden shadow-lg">
                      {track.image ? (
                        <img src={track.image} alt={track.name} className="w-full h-full object-cover" />
                      ) : (
                        <span>🎵</span>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Get all tracks after the current one for the queue
                        const remainingTracks = data.data.slice(index + 1);
                        playTrackWithQueue(track, remainingTracks);
                      }}
                      className="absolute inset-0 bg-black/60 backdrop-blur-sm rounded-lg sm:rounded-xl transition-all flex items-center justify-center hover:bg-black/80"
                    >
                      <Play size={20} className="sm:w-6 sm:h-6 text-white drop-shadow-lg" fill="white" />
                    </button>
                  </div>

                  {/* Track Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-sm sm:text-base text-white group-hover:text-primary-400 transition-colors truncate">
                      {track.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-400 truncate">
                      {track.description || formatDuration(track.duration)}
                    </p>
                  </div>

                  {/* Duration - hidden on mobile */}
                  <div className="hidden sm:flex items-center gap-1 text-gray-400 text-sm">
                    <Clock size={16} />
                    <span>{formatDuration(track.duration)}</span>
                  </div>

                  {/* Like Button */}
                  {isAuthenticated && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleLike(track._id);
                      }}
                      className={`p-2 rounded-lg transition-all ${
                        isLiked(track._id)
                          ? 'text-red-500 hover:text-red-400'
                          : 'text-gray-400 hover:text-red-500'
                      }`}
                      title={isLiked(track._id) ? t('tracks.unlike') : t('tracks.like')}
                    >
                      <Heart
                        size={18}
                        className={isLiked(track._id) ? 'fill-current' : ''}
                      />
                    </button>
                  )}

                  {/* Play Count - hidden on mobile */}
                  <div className="hidden md:block text-gray-400 text-sm">
                    {track.plays.toLocaleString()} {t('tracks.plays')}
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Pagination */}
          {data.total > 20 && (
            <div className="flex justify-center gap-2 sm:gap-3 mt-6 sm:mt-8">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 sm:px-6 py-2.5 sm:py-3 bg-white/5 border border-white/10 rounded-lg sm:rounded-xl text-white text-sm sm:text-base hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all font-medium"
              >
                {t('tracks.previous')}
              </button>
              <span className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg sm:rounded-xl font-bold shadow-lg text-sm sm:text-base">
                {page}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page * 20 >= data.total}
                className="px-4 sm:px-6 py-2.5 sm:py-3 bg-white/5 border border-white/10 rounded-lg sm:rounded-xl text-white text-sm sm:text-base hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all font-medium"
              >
                {t('tracks.next')}
              </button>
            </div>
          )}
        </>
      )}

      {/* Empty State */}
      {!isLoading && data && data.data.length === 0 && (
        <div className="text-center py-12 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
          <div className="text-6xl mb-4">🎵</div>
          <h3 className="text-xl font-bold text-white mb-2">{t('tracks.no_tracks')}</h3>
          <p className="text-gray-400">
            {search ? t('tracks.no_tracks_search') : t('tracks.no_tracks_empty')}
          </p>
        </div>
      )}
    </div>
  );
}
